import { BaseService } from '@common/http-layer/service/base.service'
import { useLocation } from '@common/location-layer/composables/useLocation'

import type { ICreateInvitationPayload, IInvitation } from '../types/invitation'

/**
 * Invitations service — handles all group invitation API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Endpoints: /{locationId}/groups/{groupId}/invitations
 */
export class InvitationsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '',
      serviceName: 'InvitationsService',
    })
  }

  private endpoint(groupId: string, path: string = '', locationId?: string): string {
    if (locationId) {
      return `/${locationId}/groups/${groupId}/invitations${path}`
    }
    const { store: state } = useLocation()
    if (!state.locationId) {
      throw new Error('[InvitationsService] Location ID is required')
    }
    return `/${state.locationId}/groups/${groupId}/invitations${path}`
  }

  async create(groupId: string, payload: ICreateInvitationPayload, locationId?: string): Promise<IInvitation> {
    const { data } = await this.post<IInvitation>(this.endpoint(groupId, '', locationId), payload, { groupId, locationId })
    return data
  }

  async acceptInvitation(groupId: string, invitationId: string, locationId?: string): Promise<unknown> {
    // invitationId comes from the URL query — encode so a crafted token cannot
    // alter the request path.
    const { data } = await this.post<unknown>(
      this.endpoint(groupId, `/${encodeURIComponent(invitationId)}/accept`, locationId),
      {},
      { groupId, locationId }
    )
    return data
  }

  async findById(groupId: string, invitationId: string, locationId?: string): Promise<IInvitation> {
    const { data } = await this.get<IInvitation>(this.endpoint(groupId, `/${encodeURIComponent(invitationId)}`, locationId), { locationId })
    return data
  }

  async deleteInvitation(groupId: string, invitationId: string, locationId?: string): Promise<unknown> {
    const { data } = await this.delete<unknown>(this.endpoint(groupId, `/${encodeURIComponent(invitationId)}`, locationId), { locationId })
    return data
  }
}
